import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Loader } from '@els/els-react--loader';
import { Modal } from '@els/els-react--modal';
import history from '@AppRoutes/history';
import RsTable from '@Pages/RetroSynthesis/components/RsTable';
import RsPaging from '@Pages/RetroSynthesis/components/RsPaging';
import { scrollTop } from '@Pages/RetroSynthesis/utils';
import { paths } from '@AppRoutes/constants';
import Footer from '@Components/Footer';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';
import ConditionsToolbar from './components/ConditionsToolbar';
import ConditionsTableHeader from './components/ConditionsTableHeader';
import ConditionsTableRow from './components/ConditionsTableRow';
import useConditionsFocusRestoration, { scheduleConditionsFocusRestoration } from './hooks/useConditionsFocusRestoration';
import { CONDITIONS_PAGE_TITLE } from './constants';
import {
  trackNavigatorDelete,
  trackNavigatorDrawReactions,
  trackNavigatorEdit,
  trackNavigatorConditionsButton,
  trackNavigatorProjectSort,
  trackNavigatorDateSort,
} from './analytics';
import './ConditionsNavigator.scss';

const idPropType = PropTypes.oneOfType([PropTypes.string, PropTypes.number]);

function getWarningMessages(locationState = {}) {
  return Array.isArray(locationState?.conditionWarningMessages)
    ? Array.from(new Set(locationState.conditionWarningMessages.filter(Boolean)))
    : [];
}

function ConditionsNavigator({
  runs = [],
  sort = [],
  selectedIds = [],
  announcement = '',
  error = '',
  isLoading = false,
  projectsHasSettledOnce = false,
  page = 0,
  pageSize = 15,
  totalItems = 0,
  totalPages = 1,
  navigatorRestoration = null,
  fetchProjects = () => null,
  setSort = () => null,
  setPaging = () => null,
  setSelectedIds = () => null,
  setAnnouncement = () => null,
  renameProject = () => false,
  deleteProjects = () => null,
  setNavigatorRestoration = () => null,
  consumeNavigatorRestoration = () => null,
  setBlockingNavigation = () => null,
}) {
  const tableWrapperRef = useRef(null);
  const pageUpdateScrollRef = useRef({ isPending: false, hasSeenLoading: false });
  const rememberAnchor = useConditionsFocusRestoration(setNavigatorRestoration);
  const isProjectsPending = !projectsHasSettledOnce || isLoading;
  const [noConditionsOpen, setNoConditionsOpen] = useState(Boolean(history.location.state?.noConditionsFound));
  const [warningMessages, setWarningMessages] = useState(() => getWarningMessages(history.location.state));

  useEffect(() => {
    fetchProjects({ page, pageSize, sort });
  }, [fetchProjects, page, pageSize, sort]);

  useEffect(() => {
    if (!pageUpdateScrollRef.current.isPending) return;

    if (isProjectsPending) {
      pageUpdateScrollRef.current.hasSeenLoading = true;
      return;
    }

    if (!pageUpdateScrollRef.current.hasSeenLoading) return;

    pageUpdateScrollRef.current = { isPending: false, hasSeenLoading: false };

    if (!error) {
      scrollTop(tableWrapperRef.current, false);
    }
  }, [error, isProjectsPending, runs]);

  // One-shot modal flags are carried as router state from the predict flow. Scrub them
  // after capturing so a refresh or browser-back on this entry doesn't re-open the modal.
  useEffect(() => {
    if (history.location.state?.noConditionsFound || getWarningMessages(history.location.state).length > 0) {
      history.replace({ ...history.location, state: undefined });
    }
  }, []);

  useEffect(() => {
    if (!navigatorRestoration?.focusTargetId || !projectsHasSettledOnce || isLoading) return undefined;

    return scheduleConditionsFocusRestoration(navigatorRestoration, consumeNavigatorRestoration, {
      scrollContainer: tableWrapperRef.current,
    });
  }, [consumeNavigatorRestoration, isLoading, navigatorRestoration, projectsHasSettledOnce]);

  const safePage = Math.min(page, totalPages - 1);
  const pagedItems = runs;
  // FR-2.2: select-all reflects the current page only.
  const isAllSelected = pagedItems.length > 0 && pagedItems.every((run) => selectedIds.includes(run.id));

  const handleSortChange = ({ sort: newSort }) => {
    if (newSort) {
      if (newSort.split(',')[0] === 'date') {
        trackNavigatorDateSort();
      } else {
        trackNavigatorProjectSort();
      }
      setSort(newSort);
    }
  };

  const handlePagingChange = ({ page: newPage, size: newSize }) => {
    pageUpdateScrollRef.current = { isPending: true, hasSeenLoading: false };
    setPaging({ page: newPage, size: newSize }, pageSize);
  };

  // FR-2.2 / FR-2.3: select-all toggles only the current page's rows; selections on other
  // pages are preserved.
  const handleSelectAll = () => {
    const pageIds = pagedItems.map((run) => run.id);
    if (isAllSelected) {
      setSelectedIds(selectedIds.filter((id) => !pageIds.includes(id)));
      setAnnouncement('Selections on this page cleared.');
    } else {
      setSelectedIds([...selectedIds, ...pageIds.filter((id) => !selectedIds.includes(id))]);
      setAnnouncement(`${pageIds.length} item${pageIds.length !== 1 ? 's' : ''} selected on this page.`);
    }
  };

  const handleSelectionChange = (id, checked) => {
    setSelectedIds(checked ? [...selectedIds, id] : selectedIds.filter((i) => i !== id));
  };

  const handleOpen = (run) => {
    trackNavigatorConditionsButton();
    rememberAnchor(`conditions-open-${run.id}`);
    history.push(`${paths.CONDITIONS}/${run.id}`);
  };

  // FR-2.4: blank/whitespace names are rejected before the network call; the row reverts.
  // On success the matching row is updated in place — no re-fetch, no reorder (AR-2.2).
  const handleRename = async (id, newName) => renameProject(id, newName);

  const performDelete = async (idsToDelete) => {
    await deleteProjects(idsToDelete);
  };

  // FR-2.9: deletion is confirmed via a modal before any network call, mirroring the
  // Retrosynthesis Projects confirm-delete dialog.
  const handleDelete = () => {
    trackNavigatorDelete();
    const idsToDelete = [...selectedIds];
    if (!idsToDelete.length) return;

    const projectsText = idsToDelete.length === 1 ? 'this project' : `${idsToDelete.length} projects`;
    const text = `Are you sure that you want to delete ${projectsText} ?`;

    NotifyDialogsService.openModalWindow({
      title: 'Confirm deletion',
      text,
      size: 'md',
      cancelText: 'Cancel',
      confirmText: 'Yes',
      type: 'info',
      backdrop: 'static',
      onConfirm: () => performDelete(idsToDelete),
    });
  };

  const handleEdit = (run) => {
    trackNavigatorEdit();
    history.push({
      pathname: paths.CONDITIONS_DRAW,
      state: {
        referenceLabel: run.referenceLabel,
        referenceType: run.referenceType,
        initialStructure: run.reactionString,
        editRunId: run.id,
      },
    });
  };

  return (
    <div className="conditions-navigator">
      {/* FR-2.9: blocking loader covers the delete (and load) request; hidden on success or failure. */}
      <Loader isVisible={isProjectsPending} isBlocking />
      <main className="conditions-navigator__body" id="main-content">
        <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
          {announcement}
        </div>
        {error && (
          <div className="conditions-navigator__error" role="alert">
            {error}
          </div>
        )}
        <div className="conditions-navigator__heading">
          <h1 id="conditions-navigator-title" className="u-els-font-size-intro">
            {CONDITIONS_PAGE_TITLE}
          </h1>
          <h2 className="u-els-font-size-body-large">
            Reagent, catalyst and solvent conditions extracted from published Reaxys reactions - for a reaction you draw, a Retrosynthesis
            step, or a Reaxys search result.
          </h2>
        </div>

        <ConditionsToolbar
          selectedCount={selectedIds.length}
          isAllSelected={isAllSelected}
          onSelectAll={handleSelectAll}
          onDelete={handleDelete}
          onNewSearch={() => {
            trackNavigatorDrawReactions();
            history.push(paths.CONDITIONS_DRAW);
          }}
        />

        {!isProjectsPending && runs.length === 0 ? (
          <>
            <div className="conditions-navigator__empty">
              <p>No condition searches yet.</p>
              <p>
                Use <strong>New condition search</strong> to start a standalone search, or click <strong>Explore conditions</strong> from a
                Retrosynthesis route step.
              </p>
            </div>
            <Footer />
          </>
        ) : (
          <RsTable
            className="rx-project-table"
            isLoading={false}
            dataList={pagedItems}
            tableAriaLabelledBy="conditions-navigator-title"
            showFooter
            wrapperRef={tableWrapperRef}
            renderItem={(run) => (
              <ConditionsTableRow
                key={run.id}
                run={run}
                onOpen={handleOpen}
                onRename={handleRename}
                onEdit={handleEdit}
                selected={selectedIds.includes(run.id)}
                onSelectionChange={handleSelectionChange}
                onBeforeNavigate={rememberAnchor}
                onBusyChange={setBlockingNavigation}
              />
            )}
            header={<ConditionsTableHeader sort={sort} onSortChange={handleSortChange} />}>
            <RsPaging
              pageSize={pageSize}
              currentPage={safePage + 1}
              totalPages={totalPages}
              totalItems={totalItems}
              onChange={handlePagingChange}
            />
          </RsTable>
        )}
      </main>
      {noConditionsOpen && (
        <Modal id="conditions-no-results" onClose={() => setNoConditionsOpen(false)} a11y={{ name: 'No conditions found' }}>
          <div className="conditions-navigator__no-results-modal">No conditions due to no similar example has been found.</div>
        </Modal>
      )}
      {warningMessages.length > 0 && (
        <Modal id="conditions-warning" onClose={() => setWarningMessages([])} a11y={{ name: 'Condition search warning' }}>
          <div className="conditions-navigator__no-results-modal">
            {warningMessages.map((message) => (
              <p key={message}>{message}</p>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
}

ConditionsNavigator.propTypes = {
  runs: PropTypes.arrayOf(PropTypes.shape({ id: idPropType })),
  sort: PropTypes.arrayOf(PropTypes.shape({ property: PropTypes.string, direction: PropTypes.string })),
  selectedIds: PropTypes.arrayOf(PropTypes.string),
  announcement: PropTypes.string,
  error: PropTypes.string,
  isLoading: PropTypes.bool,
  projectsHasSettledOnce: PropTypes.bool,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  totalItems: PropTypes.number,
  totalPages: PropTypes.number,
  navigatorRestoration: PropTypes.shape({
    focusTargetId: PropTypes.string,
  }),
  fetchProjects: PropTypes.func,
  setSort: PropTypes.func,
  setPaging: PropTypes.func,
  setSelectedIds: PropTypes.func,
  setAnnouncement: PropTypes.func,
  renameProject: PropTypes.func,
  deleteProjects: PropTypes.func,
  setNavigatorRestoration: PropTypes.func,
  consumeNavigatorRestoration: PropTypes.func,
  setBlockingNavigation: PropTypes.func,
};

export default ConditionsNavigator;
